import { useQueries } from '@tanstack/react-query';
import {
  type ConfigPolicyStopReasonDto,
  configControllerGetPublicProductConfigQueryOptions,
} from 'korint-sdk';
import { useKorintProductConfig } from 'korint-sdk/react';

// Product config only changes on deploy; without this the fan-out refetches on every mount and
// window refocus. Matches PRODUCTS_CONFIG_STALE_TIME in useKorintProductConfig.
const PRODUCTS_CONFIG_STALE_TIME = 5 * 60 * 1000;

/**
 * Whether any product in view has a stop reason a manager must validate. Declaring
 * `requiredDocuments` on a reason is the whole trigger (stop-documents.service), so a tenant
 * with none can never produce a stop awaiting validation and is offered no filter for it.
 */
export const useHasValidatedStopReasons = (): boolean => {
  const { productsConfig } = useKorintProductConfig();

  return useQueries({
    queries: Object.keys(productsConfig).map(productId => ({
      ...configControllerGetPublicProductConfigQueryOptions(productId),
      staleTime: PRODUCTS_CONFIG_STALE_TIME,
      // useQueries loses inference through the spread, so the shape is named explicitly.
      select: ({ policyStopReasons }: { policyStopReasons?: ConfigPolicyStopReasonDto[] }) =>
        policyStopReasons?.some(({ requiredDocuments }) => (requiredDocuments?.length ?? 0) > 0) ??
        false,
    })),
    combine: results => results.some(({ data }) => data === true),
  });
};
